import { Observable } from "rxjs";
import {
  FoodItemAvailability,
  FoodOrderHistory,
  FoodOrderShallow,
  NotificationStatus,
  OrderReview,
  OrderStatuses,
  RestaurantNotification,
} from "src/app/pages/merchant/food-order/models";
import {
  AdjustmentDTO,
  OrderDetail,
} from "src/app/pages/merchant/food-order/models/food-order.detail";

export enum ItemStatuses {
  inStock = "IN_STOCK",
  soldOut = "SOLD_OUT",
  soldOutUtilTmrw = "SOLD_OUT_UTIL_TMRW",
}

export abstract class FoodOrderService {
  abstract getItemAvaiList(
    page: number,
    limit: number,
    catIds?: string[],
    search?: string,
    isSoldOut?: boolean,
  ): Observable<{
    meta: {
      total: number;
      soldOutTotal: number;
    };
    data: FoodItemAvailability[];
  }>;

  abstract updateItemAvailability(
    itemId: string,
    status: ItemStatuses,
    utilTo?: Date,
  ): Observable<void>;

  abstract getHistoryOrder(
    limit: number,
    cursor: string | null,
    filter: {
      statuses: OrderStatuses[];
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
  ): Observable<{
    data: FoodOrderHistory[];
    nextCursor: string | null;
  }>;

  abstract getOrderList(
    limit: number,
    cursor: string | null,
    filter: {
      statuses: OrderStatuses[];
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
    endpoint?: string,
  ): Observable<{
    data: FoodOrderShallow[];
    nextCursor: string | null;
  }>;
  abstract mapToFoodOrderShallow(order: any): FoodOrderShallow;
  abstract getOrderLists(
    limit: number,
    statuses: OrderStatuses[],
    filter: {
      dateRange?: {
        start: Date;
        end: Date;
      };
      search?: string;
    },
    endpoint?: string,
  ): Observable<
    Record<
      string,
      {
        data: FoodOrderShallow[];
        nextCursor: string | null;
      }
    >
  >;

  abstract markOrderReady(orderId: string): Observable<void>;
  abstract markOrderPreparing(orderId: string): Observable<void>;

  abstract getOrderDetail(id: string): Observable<OrderDetail>;

  abstract updateOrderItemAdjustment(
    orderId: string,
    orderItemId: string,
    adjustments: AdjustmentDTO[],
  ): Observable<void>;

  abstract notifyCustomerModifier(orderId: string): Observable<void>;

  abstract updatePrice(
    orderId: string,
    amount: number,
    reason?: string,
  ): Observable<void>;

  abstract updateReadyTime(
    orderId: string,
    newReadyTime: Date,
  ): Observable<Date>;

  abstract updatePreparingTime(
    orderId: string,
    duration: number,
  ): Observable<Date>;

  abstract cancelOrder(orderId: string, reason: string): Observable<void>;

  abstract acceptOrder(
    orderId: string,
    estimatedPreparingTime: number,
  ): Observable<void>;
  abstract completeOrder(orderId: string): Observable<void>;
  abstract rejectOrder(orderId: string): Observable<void>;
  abstract getListReview(params: {
    ratings: number[];
    hasContent: boolean;
    hasReply: boolean;
    nextCursor?: string;
    limit: number;
  }): Observable<{
    data: OrderReview[];
    nextCursor: string;
    total: number;
  }>;

  abstract getNotifications(params: {
    nextCursor?: string;
    limit: number;
    status: NotificationStatus;
  }): Observable<{
    data: RestaurantNotification[];
    nextCursor: string;
    total: number;
  }>;

  abstract markNotificationAsRead(notificationId: string): Observable<void>;

  abstract replyReview(
    orderId: string,
    content: string,
    selectDiscount: number,
  ): Observable<void>;

  abstract getDiscountSetting(): Observable<{
    orderSuggestAmounts: number[];
  }>;
}
